import { Close } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import React, { useEffect, useState } from "react";
import { useGetApi } from "../../../hooks/useGetApi";
import { getItemApi } from "../../../services/item.service";
import { getUserApi } from "../../../services/user.service";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { addDoctorVisitByVisitIdApi } from "../../../services/consultation.service";

const DoctorVisitModal = ({ open, onClose, id }) => {
  const initialState = {
    item: null,
    doctor: null,
    date: dayjs(),
    amount: 0,
  };
  const [gridItems, setGridItems] = useState([initialState]);
  const [loading, setLoading] = useState(false);

  const { dataList: itemList } = useGetApi({
    apiFunction: getItemApi,
    params: {
      limit: Number.MAX_SAFE_INTEGER,
      type: "Other",
    },
    dataKey: "items",
  });

  const { dataList: doctorsList } = useGetApi({
    apiFunction: getUserApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "users",
  });

  const handleChange = (index, name, value) => {
    setGridItems((prevItems) =>
      prevItems.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [name]: value };

          // Update amount when item changes
          if (name === "item" && value && itemList) {
            const selectedItem = itemList.find(
              (singleItem) => singleItem._id === value
            );
            updatedItem.amount = selectedItem?.normalRate || 0;
          }

          return updatedItem;
        }
        return item;
      })
    );
  };

  const handleAddItem = () => {
    setGridItems((prevItems) => [...prevItems, initialState]);
  };

  const handleRemoveItem = (index) => {
    setGridItems((prevItems) => prevItems.filter((_, i) => i !== index));
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (id) {
      let response;

      setLoading(true);

      response = await addDoctorVisitByVisitIdApi(id, gridItems);

      setLoading(false);

      if (response?.code === 200 || response?.code === 201) {
        toast.success(`Doctor Visit added successfully`);
        setGridItems([initialState]);
        onClose();
      } else {
        toast.error(response?.message || "Some error occurred.");
      }
    } else {
      toast.error("Visit ID is required.");
    }
  };

  useEffect(() => {
    setGridItems([initialState]);
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          minWidth: "90vw", // Increased modal width
          maxWidth: "95vw", // Set max width to prevent overflow
          bgcolor: "#E5F4FF",
        },
      }}
    >
      <DialogContent>
        <Box component={"form"} onSubmit={submitHandler}>
          {/* dialog title */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: "20px",
            }}
          >
            <Typography variant="h6" sx={{ color: "#5D6A82" }}>
              Doctor Visit Charges
            </Typography>
            <Close
              onClick={onClose}
              sx={{ cursor: "pointer", color: "#5D6A82" }}
            />
          </Box>

          {/* dynamic grid items */}
          {gridItems?.map((item, index) => (
            <Grid
              container
              spacing={3}
              key={index}
              sx={{
                mb: index !== gridItems?.length - 1 ? "40px" : "20px",
              }}
            >
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Autocomplete
                  options={itemList || []}
                  getOptionLabel={(option) =>
                    `${option.itemName} (${option.itemType})`
                  }
                  value={
                    itemList.find(
                      (singleItem) => singleItem._id === item.item
                    ) || null
                  }
                  onChange={(_, newValue) =>
                    handleChange(index, "item", newValue ? newValue._id : null)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Item Name"
                      required
                      fullWidth
                    />
                  )}
                  sx={{ background: "white" }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Autocomplete
                  options={doctorsList || []}
                  getOptionLabel={(option) =>
                    `${option.firstName} ${option.lastName} (${option.userId})`
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Doctor Name" />
                  )}
                  value={
                    doctorsList.find((doctor) => doctor._id === item.doctor) ||
                    null
                  }
                  onChange={(_, newValue) =>
                    handleChange(
                      index,
                      "doctor",
                      newValue ? newValue._id : null
                    )
                  }
                  sx={{ background: "white" }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <DatePicker
                  label="Date"
                  value={item.date ? dayjs(item.date) : null}
                  onChange={(newDate) => {
                    handleChange(index, "date", newDate?.toISOString());
                  }}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                  sx={{ background: "white" }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <TextField
                  label="Amount"
                  name="amount"
                  type="number"
                  value={item.amount}
                  onChange={(e) =>
                    handleChange(index, "amount", e.target.value)
                  }
                  required
                  fullWidth
                  sx={{ background: "white" }}
                />
              </Grid>

              {/* Only show remove button if it's not the first item */}
              {gridItems?.length > 1 && index !== 0 && (
                <Box sx={{ textAlign: "right", width: "100%" }}>
                  <Button
                    variant="text"
                    onClick={() => handleRemoveItem(index)}
                    color="error"
                    sx={{ ml: "25px", mt: "5px", textTransform: "none" }}
                  >
                    Remove
                  </Button>
                </Box>
              )}
            </Grid>
          ))}

          {/* add grid item */}
          <Button
            onClick={handleAddItem}
            variant="text"
            sx={{ color: "#92A6CC", textTransform: "none" }}
          >
            + Add Doctor Visit
          </Button>

          {/* submit button */}
          <Box sx={{ my: "20px", textAlign: "center" }}>
            <Button
              variant="contained"
              sx={{ width: "150px", textTransform: "none" }}
              type="submit"
              disabled={loading}
            >
              {loading ? <CircularProgress size={"20px"} /> : "Submit"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DoctorVisitModal;
